import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaComments } from "react-icons/fa6";
import { useU2000AuthContext } from "../../../context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../../context/logicalUnit/U2000LogicalUnitContext";
import U2000SearchPlaceholder from "../../U2000SearchPlaceholder";
import U2000UserProfilePopup from "../../popup/U2000UserProfilePopup";
import U2000HelpIcon from "../../common/U2000HelpIcon";
import U2000NotificationIcon from "../../notification/U2000NotificationIcon";
import { extractLogicalUnitCode } from "../../../tools/U2000GetUserDocPath";

import type { FC } from "react";

const U3204_READ_ROLE = "U3204:READ";

const U2000Header: FC = () => {
  const { auth, hasRole } = useU2000AuthContext();
  const { logicalUnitTitle } = useU2000LogicalUnitContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [userName, setUserName] = useState<string>("");
  const [isUserPopupVisible, setIsUserPopupVisible] = useState<boolean>(false);
  const canOpenHelpChat = hasRole(U3204_READ_ROLE);

  // Extraire le code de l'unité logique depuis l'URL
  const logicalUnitCode = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    // Chercher un segment qui correspond à une unité logique (U3101, U2101, etc.)
    for (const segment of pathSegments) {
      const code = extractLogicalUnitCode(segment);
      if (code) {
        return code;
      }
    }
    return null;
  }, [location.pathname]);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      setUserName(auth?.user.firstName + " " + auth?.user.lastName);
    }
  }, [auth]);

  const toggleUserPopup = () => {
    setIsUserPopupVisible(!isUserPopupVisible);
  };

  const closeUserPopup = () => {
    setIsUserPopupVisible(false);
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-xs">
      {/* Section Gauche - Vide */}
      <div className="flex items-center"></div>

      {/* Section Centre - Logical Unit Title (left aligned) — toujours visible (liste + formulaire) */}
      <div className="flex-1 text-left flex items-center gap-3">
        <h1
          className="text-xl sm:text-2xl md:text-3xl font-semibold text-emerald-600 leading-tight truncate max-w-[65%]"
          title={logicalUnitTitle ?? ""}
          aria-live="polite"
        >
          {logicalUnitTitle ?? ""}
        </h1>
        {logicalUnitCode && (
          <U2000HelpIcon logicalUnitCode={logicalUnitCode} size={22} className="shrink-0" />
        )}
      </div>

      {/* Section Droite - Recherche + Actions utilisateur */}
      <div className="flex items-center gap-4 relative">
        {/* Recherche */}
        <U2000SearchPlaceholder />

        {/* Aide usager (chat U3204 / AIDE_USAGER) — distinct de U2000HelpIcon (docs HTML) */}
        {canOpenHelpChat && (
          <button
            type="button"
            onClick={() => navigate("/U3204?mission=AIDE_USAGER")}
            className="relative p-2 rounded-lg transition-all duration-200 hover:bg-gray-50 text-gray-600"
            aria-label={t("helpChat")}
            title={t("helpChat")}
          >
            <FaComments className="h-5 w-5" aria-hidden />
          </button>
        )}

        {/* Notifications */}
        <U2000NotificationIcon />

        {/* Profil utilisateur */}
        <div className="relative">
          <button onClick={toggleUserPopup} className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-2">
              {userName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            <span className="text-sm font-medium text-gray-900">{userName}</span>
          </button>

          <U2000UserProfilePopup isVisible={isUserPopupVisible} onClose={closeUserPopup} />
        </div>
      </div>
    </header>
  );
};

export default U2000Header;
